import type { GenericError } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useState } from 'react';
import { getCurrentUser, setCurrentUser } from '../utils/currentUser';
import { useLogger } from './useLogger';

// getAccessTokenSilently rejects with Auth0's GenericError, which does extend
// Error and carries error / error_description. The fields stay optional because
// a non-Auth0 failure (e.g. a TypeError) can surface here as well.
type Auth0TokenError = Error &
    Partial<Pick<GenericError, 'error' | 'error_description'>>;

export const useAuthToken = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const logger = useLogger();
    const [
        isUserAuthenticatedWithHustroApp,
        setIsUserAuthenticatedWithHustroApp,
    ] = useState(false);
    const [isTokenLoading, setIsTokenLoading] = useState(false);

    const getToken = async () => {
        setIsTokenLoading(true);
        try {
            const token = await getAccessTokenSilently();
            if (token) {
                localStorage.setItem('token', token);
                setCurrentUser({ email: user?.email });
            }
        } catch (e: unknown) {
            const auth0Error = e as Auth0TokenError;
            if (auth0Error?.error === 'login_required') {
                return;
            }
            if (auth0Error?.error === 'consent_required') {
                setIsUserAuthenticatedWithHustroApp(true);
            } else {
                // Silent refresh failures block /user/me, which is where the
                // email for Sentry comes from — log enough to diagnose them.
                logger.error('Error getting token', auth0Error, {
                    auth0Error: auth0Error?.error,
                    auth0ErrorDescription: auth0Error?.error_description,
                    hasAuth0User: Boolean(user),
                    hasKnownEmail: Boolean(getCurrentUser().email),
                });
            }
        } finally {
            setIsTokenLoading(false);
        }
    };

    return {
        getToken,
        isTokenLoading,
        isUserAuthenticatedWithHustroApp,
    };
};
